import { type FC, useCallback, useEffect, useMemo, useState } from 'react'

import { Box, Divider, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Maximize } from '@factorialco/f0-react/icons/app'
import { addMonths, subMonths } from 'date-fns'
import {
  SalaryAdvanceAccrualBreakdownDocument,
  SalaryAdvanceEmployeeProfileFragment,
  SalaryAdvanceEmployeeRequestsDocument,
} from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import PageLoading from 'modules/layout/components/Page/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { ContractBreakdownCard } from './ContractBreakdownCard'
import { MonthSelector } from './MonthSelector'
import { RequestsList } from './RequestsList'
import { SummarySection } from './SummarySection'
import type { RequestNode } from './types'

interface Props {
  open: boolean
  onClose: () => void
  employee: SalaryAdvanceEmployeeProfileFragment | null
}

const buildUtcMonthRange = (month: Date) => {
  const year = month.getFullYear()
  const monthIndex = month.getMonth()

  return {
    createdAfter: new Date(Date.UTC(year, monthIndex, 1, 0, 0, 0, 0)).toISOString(),
    createdBefore: new Date(Date.UTC(year, monthIndex + 1, 0, 23, 59, 59, 999)).toISOString(),
  }
}

const isRequestInScope = (
  request: RequestNode,
  employeeId: string,
  createdAfter: string,
  createdBefore: string
) => {
  if (String(request.employeeId) !== employeeId) return false

  const createdAt = new Date(request.createdAt).getTime()
  const scopeStart = new Date(createdAfter).getTime()
  const scopeEnd = new Date(createdBefore).getTime()

  return createdAt >= scopeStart && createdAt <= scopeEnd
}

const buildMonthlyTimesheetParams = (date: Date) => ({
  periodType: 'monthly' as const,
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate(),
})

export const AccrualBreakdownSidePanel: FC<Props> = ({ open, onClose, employee }) => {
  const { navigation } = useNavigation()
  const { access } = useCurrentSession()
  const locale = access.locale || 'en'
  const employeeId = useMemo(() => {
    const currentEmployeeId = employee?.employeeId ?? employee?.employeeName?.id
    return currentEmployeeId ? String(currentEmployeeId) : null
  }, [employee?.employeeId, employee?.employeeName?.id])
  const employeeIds = useMemo(() => (employeeId ? [employeeId] : []), [employeeId])

  const [activeMonth, setActiveMonth] = useState(() => new Date())
  const [openVersion, setOpenVersion] = useState(0)
  const { createdAfter, createdBefore } = useMemo(
    () => buildUtcMonthRange(activeMonth),
    [activeMonth]
  )

  useEffect(() => {
    if (open) {
      setActiveMonth(new Date())
      setOpenVersion((current) => current + 1)
    }
  }, [employeeId, open])

  const { data, loading, error } = useQuery(SalaryAdvanceAccrualBreakdownDocument, {
    variables: { employeeId: employeeId ?? '' },
    skip: !open || !employeeId,
    fetchPolicy: 'network-only',
  })

  const {
    data: requestsData,
    loading: requestsLoading,
    error: requestsError,
  } = useQuery(SalaryAdvanceEmployeeRequestsDocument, {
    variables: {
      employeeIds,
      createdAfter,
      createdBefore,
    },
    skip: !open || !employeeId,
    fetchPolicy: 'cache-and-network',
  })

  const breakdownNode = data?.salaryAdvance?.accrualBreakdownsConnection?.edges?.[0]?.node
  const breakdown =
    breakdownNode && employeeId && breakdownNode.employeeId === employeeId ? breakdownNode : null
  const requests = useMemo(() => {
    const nodes = requestsData?.salaryAdvance?.requestsConnection?.edges?.map((e) => e.node) ?? []

    if (!employeeId) return []

    return [...nodes]
      .filter((request) => isRequestInScope(request, employeeId, createdAfter, createdBefore))
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
  }, [createdAfter, createdBefore, employeeId, requestsData])
  const currency = employee?.currency ?? 'EUR'

  const formatMoney = useCallback(
    (cents: number | null | undefined) => {
      if (cents == null) return '-'
      return MoneyWithCurrency({ amount: cents, currency, locale })
    },
    [currency, locale]
  )

  const formatMinutes = useCallback((minutes: number | null | undefined) => {
    if (minutes == null) return '-'
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return i18n.t('salary_advance.employee.breakdown_side_panel.time_format', {
      hours,
      minutes: mins,
    })
  }, [])

  const employeeName = employee?.employeeName
    ? `${employee.employeeName.firstName} ${employee.employeeName.lastName}`
    : ''
  const sidepanelTitle = employeeName
    ? `${i18n.t('salary_advance.employee.breakdown_side_panel.title')} - ${employeeName}`
    : i18n.t('salary_advance.employee.breakdown_side_panel.title')
  const sortedContractBreakdowns = useMemo(() => {
    if (!breakdown?.contractBreakdowns?.length) return []

    return [...breakdown.contractBreakdowns].sort((a, b) => {
      const endCmp = (b.periodEnd || '').localeCompare(a.periodEnd || '')
      if (endCmp !== 0) return endCmp

      const startCmp = (b.periodStart || '').localeCompare(a.periodStart || '')
      if (startCmp !== 0) return startCmp

      return String(b.contractVersionId).localeCompare(String(a.contractVersionId), undefined, {
        numeric: true,
      })
    })
  }, [breakdown])
  const hasHourlyAccrual = useMemo(
    () => sortedContractBreakdowns.some((contract) => contract.strategy === 'hourly'),
    [sortedContractBreakdowns]
  )
  const openTimesheetLabel = i18n.t(
    'time_tracking.table_collection.employee_summary.actions.open_timesheet'
  )
  const employeeTimesheetPath =
    navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.fullPath
  const handleOpenTimesheet = useCallback(() => {
    if (!employeeId) return

    const detailParams = buildMonthlyTimesheetParams(new Date())

    window.open(
      employeeTimesheetPath({
        ...detailParams,
        employeeId,
      }),
      '_blank',
      'noopener,noreferrer'
    )
  }, [employeeId, employeeTimesheetPath])

  return (
    <Sidepanel
      open={open}
      onClose={onClose}
      title={sidepanelTitle}
      closeLabel={i18n.t('common.close')}
      width="wide"
    >
      <Box gap="s24">
        <Box flexDirection="column" gap="s16">
          <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" gap="s12">
            <Text size="300" weight="semibold" color="grey1000">
              {i18n.t('salary_advance.employee.breakdown_side_panel.current_month_accrual')}
            </Text>
            {employeeId && hasHourlyAccrual && (
              <F0Button
                variant="outline"
                size="sm"
                icon={Maximize}
                label={openTimesheetLabel}
                onClick={handleOpenTimesheet}
              />
            )}
          </Box>

          {!breakdown && loading && <PageLoading />}

          {!breakdown && !loading && error && (
            <Box flexDirection="column" justifyContent="center" alignItems="center" padding="s48">
              <Text size="200" color="grey700">
                {i18n.t('errors.generic_error')}
              </Text>
            </Box>
          )}

          {!breakdown && !loading && !error && (
            <Box flexDirection="column" justifyContent="center" alignItems="center" padding="s48">
              <Text size="200" color="grey700">
                {i18n.t('salary_advance.employee.breakdown_side_panel.no_data')}
              </Text>
            </Box>
          )}

          {breakdown && (
            <>
              <SummarySection breakdown={breakdown} formatMoney={formatMoney} />

              {sortedContractBreakdowns.map((contract, index) => (
                <ContractBreakdownCard
                  key={contract.contractVersionId}
                  contract={contract}
                  index={index}
                  locale={locale}
                  formatMoney={formatMoney}
                  formatMinutes={formatMinutes}
                  defaultExpanded={index === 0}
                  openVersion={openVersion}
                />
              ))}
            </>
          )}
        </Box>

        {employeeId && (
          <>
            <Divider />

            <Box flexDirection="column" gap="s16" paddingBottom="s64">
              <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" gap="s12">
                <Text size="300" weight="semibold" color="grey1000">
                  {i18n.t('salary_advance.employee.breakdown_side_panel.requests_title')}
                </Text>

                <MonthSelector
                  activeMonth={activeMonth}
                  locale={locale}
                  onPrev={() => setActiveMonth((m) => subMonths(m, 1))}
                  onNext={() => setActiveMonth((m) => addMonths(m, 1))}
                />
              </Box>

              <RequestsList
                requests={requests}
                loading={requestsLoading}
                error={!!requestsError}
                locale={locale}
              />
            </Box>
          </>
        )}
      </Box>
    </Sidepanel>
  )
}
